import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch, RootState } from "./store";

/* ──────────────────────────
   TYPES
   ────────────────────────── */
export interface CommerceOrder {
  id: string;
  guestId: string;
  hostId: string;

  items: any[];
  subtotal: number;
  totalQty: number;
  totalPaid: number;

  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;

  paymentId: string;

  isConfirmed: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isPaid: boolean;
  isDelivered: boolean;

  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
}

interface CommerceOrdersState {
  orders: CommerceOrder[];
  currentOrder: CommerceOrder | null;
  error: string | null;
  unsubscribe?: () => void;

   hostDeliveredCount: number;
  hostDeliveredProfit: number;
}

/* ──────────────────────────
   INITIAL STATE
   ────────────────────────── */
const initialState: CommerceOrdersState = {
  orders: [],
  currentOrder: null,
  error: null,
  unsubscribe: undefined,
  hostDeliveredCount: 0,
  hostDeliveredProfit: 0
};

/* ──────────────────────────
   UPSERT (Create / Update)
   ────────────────────────── */
   export const upsertCommerceOrder = createAsyncThunk<
  CommerceOrder,
  Partial<CommerceOrder> & {
    paymentId: string;
    hostId?: string;
    markSellerDelivered?: boolean;
  },
  { rejectValue: string }
>(
  "commerceOrders/upsert",
  async (orderData, { rejectWithValue }) => {
    try {
      if (!orderData.paymentId) {
        throw new Error("Missing paymentId");
      }

      const orderId = orderData.paymentId;
      const docRef = doc(db, "CommerceOrders", orderId);

      const snap = await getDoc(docRef);
      if (!snap.exists()) {
        throw new Error("Order does not exist");
      }

      const existing = snap.data() as CommerceOrder;

      const updatePayload: any = {
        updatedAt: serverTimestamp(),
      };

      /* ──────────────────────────
         MULTI-HOST DELIVERY LOGIC
         ────────────────────────── */
      if (
        orderData.markSellerDelivered === true &&
        orderData.hostId
      ) {
        const updatedItems = existing.items.map((item: any) => {
          if (item.hostId === orderData.hostId) {
            return {
              ...item,
              deliveryStatus: {
                ...item.deliveryStatus,
                sellerDelivered: true,
              },
            };
          }
          return item;
        });

        updatePayload.items = updatedItems;

        // ✅ Check if ALL hosts delivered
        const allDelivered = updatedItems.every(
          (i: any) =>
            i.deliveryStatus?.sellerDelivered === true
        );

        if (allDelivered) {
          updatePayload.isDelivered = true;
        }
      }

      /* ──────────────────────────
         OTHER SAFE FLAGS
         ────────────────────────── */

      if (typeof orderData.isConfirmed === "boolean") {
        updatePayload.isConfirmed = orderData.isConfirmed;
      }

      if (typeof orderData.isCancelled === "boolean") {
        updatePayload.isCancelled = orderData.isCancelled;
      }

      if (typeof orderData.isRefunded === "boolean") {
        updatePayload.isRefunded = orderData.isRefunded;
      }

      if (typeof orderData.isPaid === "boolean") {
        updatePayload.isPaid = orderData.isPaid;
      }

      console.log("🔥 Firestore update payload:", updatePayload);

      await updateDoc(docRef, updatePayload);

      return {
        ...existing,
        ...updatePayload,
        id: orderId,
      } as CommerceOrder;
    } catch (err: any) {
      console.error("❌ upsertCommerceOrder failed:", err);
      return rejectWithValue(
        err.message || "Failed to upsert commerce order"
      );
    }
  }
);



/* ──────────────────────────
   LISTEN (Single Order)
   ────────────────────────── */
export const listenToCommerceOrder =
  (orderId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, "CommerceOrders", orderId);

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          const { id: _ignored, ...rest } =
            snap.data() as CommerceOrder;

          dispatch(
            commerceOrdersSlice.actions.setCurrentOrder({
              id: snap.id,
              ...rest,
              createdAt: rest.createdAt?.toMillis?.() || null,
              updatedAt: rest.updatedAt?.toMillis?.() || null,
            })
          );
        } else {
          dispatch(
            commerceOrdersSlice.actions.setCurrentOrder(null)
          );
        }
      },
      (error) => {
        dispatch(
          commerceOrdersSlice.actions.setError(error.message)
        );
      }
    );

    dispatch(
      commerceOrdersSlice.actions.setUnsubscribe(unsubscribe)
    );
  };

/* ──────────────────────────
   LISTEN (Guest Orders)
   ────────────────────────── */
export const listenToCommerceGuestOrders =
  (guestId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!guestId) return;

    const { unsubscribe } = getState().commerceOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, "CommerceOrders"),
      where("guestId", "==", guestId)
    );

    const unsubscribeFn = onSnapshot(
      q,
      (snap) => {
        const orders: CommerceOrder[] = snap.docs.map((d) => {
          const { id: _ignored, ...rest } =
            d.data() as CommerceOrder;
          return {
            id: d.id,
            ...rest,
            createdAt: rest.createdAt?.toMillis?.() || null,
            updatedAt: rest.updatedAt?.toMillis?.() || null,
          };
        });

        dispatch(
          commerceOrdersSlice.actions.setOrders(orders)
        );
      },
      (error) => {
        dispatch(
          commerceOrdersSlice.actions.setError(error.message)
        );
      }
    );

    dispatch(
      commerceOrdersSlice.actions.setUnsubscribe(unsubscribeFn)
    );
  };

/* ──────────────────────────
   LISTEN (Host Orders)
   ────────────────────────── */
export const listenToCommerceHostOrders =
  (hostId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().commerceOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, "CommerceOrders"),
      where("hostId", "==", hostId)
    );

    const unsubscribeFn = onSnapshot(
      q,
      (snap) => {
        const orders: CommerceOrder[] = snap.docs.map((d) => {
          const { id: _ignored, ...rest } =
            d.data() as CommerceOrder;
          return {
            id: d.id,
            ...rest,
            createdAt: rest.createdAt?.toMillis?.() || null,
            updatedAt: rest.updatedAt?.toMillis?.() || null,
          };
        });

        dispatch(
          commerceOrdersSlice.actions.setOrders(orders)
        );
      },
      (error) => {
        dispatch(
          commerceOrdersSlice.actions.setError(error.message)
        );
      }
    );

    dispatch(
      commerceOrdersSlice.actions.setUnsubscribe(unsubscribeFn)
    );
  };


  export const fetchHostDeliveredStats = createAsyncThunk<
  {
    total: number;
    delivered: number;
    pending: number;
  },
  string,
  { rejectValue: string }
>(
  "commerceOrders/fetchHostDeliveredStats",
  async (hostId, { rejectWithValue }) => {
    try {
      if (!hostId) throw new Error("Missing hostId");

      const q = query(
        collection(db, "CommerceOrders"),
        where("hostId", "==", hostId)
      );

      const snap = await getDocs(q);

      let total = 0;
      let delivered = 0;
      let pending = 0;

      snap.forEach((doc) => {
        const data = doc.data() as CommerceOrder;
        total += 1;

        if (data.isDelivered === true) {
          delivered += 1;
        } else {
          pending += 1;
        }
      });

      return {
        total,
        delivered,
        pending,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch host order stats"
      );
    }
  }
);



/* ──────────────────────────
   SLICE
   ────────────────────────── */
const commerceOrdersSlice = createSlice({
  name: "CommerceOrders",
  initialState,
  reducers: {
    setOrders: (
      state,
      action: PayloadAction<CommerceOrder[]>
    ) => {
      state.orders = action.payload;
    },
    setCurrentOrder: (
      state,
      action: PayloadAction<CommerceOrder | null>
    ) => {
      state.currentOrder = action.payload;
    },
    setError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.error = action.payload;
    },
    setUnsubscribe: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribe = action.payload;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      upsertCommerceOrder.fulfilled,
      (state, action) => {
        const idx = state.orders.findIndex(
          (o) => o.id === action.payload.id
        );

        if (idx >= 0) {
          state.orders[idx] = action.payload;
          if (
            state.currentOrder?.id === action.payload.id
          ) {
            state.currentOrder = action.payload;
          }
        } else {
          state.orders.unshift(action.payload);
        }
      }
    );

    builder.addCase(
      upsertCommerceOrder.rejected,
      (state, action) => {
        state.error =
          action.payload || "Failed to upsert commerce order";
      }
    );
  },
});

export const {
  setOrders,
  setCurrentOrder,
  setError,
  setUnsubscribe,
  clearOrders,
} = commerceOrdersSlice.actions;

export default commerceOrdersSlice.reducer;
